import React, { useState, useEffect } from 'react';
import StudySwitcher from './StudySwitcher';
import dizzarooLogo from '../../images/dizzaroo-logo-symbol-removebg-preview.png';
import studyDesignSelectionService from '../../services/studyDesignSelection.service';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Badge,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  ThemeProvider,
  createTheme,
  CssBaseline,
  alpha,
  Button,
  ListItemAvatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Description,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Logout as LogoutIcon,
  Help as HelpIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { colors, radius, shadow, transition, statusColors } from '../../theme/tokens';
import { IND_ENABLED } from '../../config/env';

// Dizzaroo Web UI Addendum v0.1 – Primary: Deep Blue #1E73BE; accents: Soft Green, Blue Green
const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: colors.primary[500],   // #1E73BE Deep Blue
      light: colors.primary.light,
      dark: colors.primary[600],   // #1E6091
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.accent.teal,
      light: colors.accent.tealLight,
      dark: colors.accent.tealDark,
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'light' ? colors.neutral[100] : '#101114',
      paper: mode === 'light' ? '#ffffff' : '#17181c',
    },
    divider: mode === 'light' ? colors.neutral[200] : 'rgba(255, 255, 255, 0.08)',
    text: {
      primary: mode === 'light' ? colors.neutral[900] : '#f2f2f3',
      secondary: mode === 'light' ? colors.neutral[700] : '#a2a3a8',
    },
    dizzaroo: {
      primary: colors.primary,
      accent: colors.accent,
      neutral: colors.neutral,
      lightGreen: mode === 'light' ? '#e8f5e8' : '#1a2e1a',
      lightBlue: mode === 'light' ? '#e3f2fd' : '#1a1f2e',
      white: '#ffffff',
      darkBackground: '#101114',
      darkPaper: '#17181c',
      darkText: '#f2f2f3',
      darkSecondary: '#a2a3a8',
    },
  },
  typography: {
    fontFamily: '"Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '0em',
    },
    h4: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '0.00735em',
    },
    h5: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '0em',
    },
    h6: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 500,
      fontSize: '0.75rem',
      letterSpacing: '0.00714em',
    },
    body1: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem',
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      letterSpacing: '0.01071em',
    },
    button: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem',
      letterSpacing: '0.02857em',
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      letterSpacing: '0.03333em',
    },
    overline: {
      fontFamily: '"Urbanist", sans-serif',
      fontWeight: 400,
      fontSize: '0.625rem',
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: radius.md,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800;900&display=swap');

        html, body {
          font-family: "Urbanist", "Inter", "SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        *::-webkit-scrollbar { width: 8px; height: 8px; }
        *::-webkit-scrollbar-track { background: transparent; }
        *::-webkit-scrollbar-thumb {
          background-color: ${mode === 'light' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.18)'};
          border-radius: 8px;
        }
        *::-webkit-scrollbar-thumb:hover {
          background-color: ${mode === 'light' ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.28)'};
        }
      `,
    },
    MuiButton: {
      defaultProps: {
        color: 'primary',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: radius.md,
          transition: `all ${transition.fast} ${transition.easing}`,
          // This app's real, currently-active theme — MainLayout wraps essentially every
          // routed page in its own <ThemeProvider>, and MUI v5 nests ThemeProviders with a
          // SHALLOW merge ({...upperTheme, ...localTheme}), so this file's `components`
          // object entirely replaces utils/theme.js's (including its MuiButton fix) for
          // everything rendered inside MainLayout — i.e. most of the app. Same bug, same
          // fix as utils/theme.js and protocol/theme.js: 'contained'/'outlined' below set
          // their own background/border/color for '&.MuiButton-colorPrimary' with the same
          // CSS specificity (one class) as this rule, and the colorPrimary rule wins the
          // tie, so a disabled primary button rendered in full color instead of looking
          // disabled. The nested '&.Mui-disabled' inside colorPrimary below (two classes)
          // is the actual fix — plain CSS specificity, order-independent. This root-level
          // rule is the fallback for any variant with no color-specific override.
          '&.Mui-disabled': {
            backgroundColor: mode === 'light' ? colors.neutral[100] : 'rgba(255,255,255,0.12)',
            color: mode === 'light' ? colors.neutral[500] : 'rgba(255,255,255,0.38)',
            borderColor: mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.12)',
          },
        },
        contained: {
          '&.MuiButton-colorPrimary': {
            backgroundColor: colors.primary[500],
            color: '#ffffff',
            '&:hover': { backgroundColor: colors.primary[600] },
            '&.Mui-disabled': {
              backgroundColor: mode === 'light' ? colors.neutral[100] : 'rgba(255,255,255,0.12)',
              color: mode === 'light' ? colors.neutral[500] : 'rgba(255,255,255,0.38)',
            },
          },
        },
        outlined: {
          '&.MuiButton-colorPrimary': {
            borderColor: colors.primary[500],
            color: colors.primary[500],
            '&:hover': { borderColor: colors.primary[600], backgroundColor: 'rgba(30, 115, 190, 0.08)' },
            '&.Mui-disabled': {
              borderColor: mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.12)',
              color: mode === 'light' ? colors.neutral[500] : 'rgba(255,255,255,0.38)',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: `background-color ${transition.fast} ${transition.easing}, color ${transition.fast} ${transition.easing}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#ffffff' : '#17181c',
        },
        elevation0: {
          border: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          margin: '2px 8px',
          padding: '8px 12px',
          transition: `background-color ${transition.fast} ${transition.easing}`,
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary[500], mode === 'light' ? 0.1 : 0.22),
            '&:hover': {
              backgroundColor: alpha(colors.primary[500], mode === 'light' ? 0.14 : 0.28),
            },
          },
          '&:hover': {
            backgroundColor: mode === 'light' ? colors.neutral[100] : alpha('#ffffff', 0.06),
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#ffffff' : '#17181c',
          borderRight: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
          border: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
          marginTop: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          margin: '2px 6px',
          '&:hover': {
            backgroundColor: mode === 'light' ? colors.neutral[100] : alpha('#ffffff', 0.06),
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'light' ? colors.neutral[900] : '#2a2b30',
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: radius.sm,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        colorDefault: {
          backgroundColor: statusColors.default.bg,
          color: statusColors.default.color,
        },
        colorPrimary: {
          backgroundColor: statusColors.submitted.bg,
          color: statusColors.submitted.color,
        },
        colorSuccess: {
          backgroundColor: statusColors.approved.bg,
          color: statusColors.approved.color,
        },
        colorWarning: {
          backgroundColor: statusColors.draft.bg,
          color: statusColors.draft.color,
        },
        colorError: {
          backgroundColor: statusColors.rejected.bg,
          color: statusColors.rejected.color,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
          boxShadow: shadow.sm,
          transition: `box-shadow ${transition.base} ${transition.easing}, border-color ${transition.base} ${transition.easing}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 2.5, borderRadius: radius.sm },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: mode === 'light' ? colors.neutral[50] : 'rgba(255,255,255,0.04)',
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:hover': {
            backgroundColor: mode === 'light' ? colors.neutral[50] : alpha('#ffffff', 0.04),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
          fontSize: '0.875rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.xl,
          boxShadow: shadow.xl,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.125rem', fontWeight: 700 },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: shadow.lg,
          border: `1px solid ${mode === 'light' ? colors.neutral[200] : 'rgba(255,255,255,0.08)'}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: `border-color ${transition.fast} ${transition.easing}`,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[500],
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.md, fontSize: '0.875rem' },
        standardSuccess: {
          backgroundColor: statusColors.approved.bg,
          color: statusColors.approved.color,
        },
        standardWarning: {
          backgroundColor: statusColors.draft.bg,
          color: statusColors.draft.color,
        },
        standardError: {
          backgroundColor: statusColors.rejected.bg,
          color: statusColors.rejected.color,
        },
        standardInfo: {
          backgroundColor: statusColors.submitted.bg,
          color: statusColors.submitted.color,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.875rem' },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${colors.primary[500]}`,
            outlineOffset: 2,
          },
        },
      },
    },
  },
});

const MainLayout = ({ children }) => {
  // Dark mode is intentionally not exposed in the UI right now — keep it fixed to light.
  const mode = 'light';
  const theme = React.useMemo(() => getTheme(mode), [mode]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  // This notification feed is still sample/placeholder content (no backend
  // notification model exists yet) — this local flag at least makes "Mark
  // all as read" do something real (clear the badge) instead of nothing.
  const [notificationsRead, setNotificationsRead] = useState(false);
  const [navMenuAnchorEl, setNavMenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isHubOwner } = useAuth();

  // Study-level tabs (Study Dashboard/Protocol/SAP/ICF/...) — shown in this top bar,
  // next to the logo, only while a study is open (?project= present in the URL).
  // Moved here from StudyFormTabs.jsx so they're reachable from the persistent header
  // instead of a tab row inside the Protocol page body.
  const studyTabQueryParams = new URLSearchParams(location.search);
  const studyProjectId = studyTabQueryParams.get('project');
  const studyTabFromUrl = studyTabQueryParams.get('tab');

  const [studySelectedDocs, setStudySelectedDocs] = useState(() => {
    try {
      const saved = localStorage.getItem(`study_selected_docs_${studyProjectId || 'default'}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (!studyProjectId) return;
    try {
      const saved = localStorage.getItem(`study_selected_docs_${studyProjectId}`);
      if (saved) setStudySelectedDocs(new Set(JSON.parse(saved)));
    } catch {
      // ignore malformed cache
    }
    studyDesignSelectionService
      .getSelection(studyProjectId)
      .then((docs) => setStudySelectedDocs(new Set(docs)))
      .catch(() => {
        // MongoDB unavailable — keep whatever localStorage value is already loaded
      });
  }, [studyProjectId]);

  // Picks up checkbox changes made in StudyFormTabs.jsx's Study Design tab
  // immediately, without waiting for a page reload to remount this component
  // and re-run the fetch above. See the dispatch site (handleDocToggle) for
  // why a plain `storage` event listener wouldn't work here — same tab/window.
  useEffect(() => {
    const handleSelectionChanged = (event) => {
      const { projectId, selectedDocs } = event.detail || {};
      if (!projectId || projectId !== studyProjectId || !Array.isArray(selectedDocs)) return;
      setStudySelectedDocs(new Set(selectedDocs));
    };
    window.addEventListener('study-selected-docs-changed', handleSelectionChanged);
    return () => window.removeEventListener('study-selected-docs-changed', handleSelectionChanged);
  }, [studyProjectId]);

  // 'app-home' (the app's actual landing page, '/') and 'workflows' (Document Workflows,
  // its own route) don't live inside StudyFormTabs' globalTab switch like the others —
  // `external` marks them so click handling / active-tab detection route around that.
  // 'app-home' also skips carrying `?project=` forward — it's meant to exit the study.
  const STUDY_TAB_ITEMS = [
    { slug: 'app-home', label: 'Home', always: true, external: true, path: '/', carryProject: false },
    { slug: 'home', label: 'Study Dashboard', always: true },
    { slug: 'protocol', label: 'Protocol', docKey: 'Protocol' },
    { slug: 'sap', label: 'SAP', docKey: 'sap' },
    { slug: 'icf', label: 'ICF', docKey: 'ICF' },
    { slug: 'ind', label: 'IND', docKey: 'IND' },
    // Hidden for now (see HIDDEN_OTHER_DOC_IDS in StudyFormTabs.jsx) — code retained for re-enable
    // { slug: 'registry-management', label: 'Registry Management', docKey: 'registry-management' },
    { slug: 'translations', label: 'Translations', docKey: 'translations' },
    { slug: 'design-studio', label: 'Design Studio', always: true },
    { slug: 'templates', label: 'Library', always: true },
    { slug: 'workflows', label: 'Document Workflows', always: true, external: true, path: '/documents/workflows' },
  ];
  const visibleStudyTabs = STUDY_TAB_ITEMS.filter(
    (item) => (item.always || studySelectedDocs.has(item.docKey)) && (IND_ENABLED || item.slug !== 'ind')
  );
  const onWorkflowsRoute = location.pathname.startsWith('/documents/workflows');
  const activeStudyTabSlug = onWorkflowsRoute
    ? 'workflows'
    : visibleStudyTabs.some((item) => item.slug === studyTabFromUrl)
      ? studyTabFromUrl
      : 'home';

  // StudyFormTabs.jsx (the internal/non-external tabs' destination) is only mounted at
  // these two routes. When the current page is some OTHER route entirely — e.g. the
  // 'workflows' external tab's own /documents/workflows — reusing location.pathname here
  // would just re-add ?tab= onto that unrelated route instead of navigating back to the
  // study form, silently stranding the user on the page they're already on.
  const STUDY_FORM_ROUTES = ['/clinical-intake', '/document-editor/study-form'];
  const studyFormBasePath = STUDY_FORM_ROUTES.includes(location.pathname)
    ? location.pathname
    : '/clinical-intake';

  const handleStudyTabChange = (_, slug) => {
    const item = STUDY_TAB_ITEMS.find((t) => t.slug === slug);
    if (item?.external) {
      navigate(item.carryProject === false ? item.path : `${item.path}?project=${studyProjectId}`);
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('tab', slug);
    // 'protocolSubTab' (written by StudyFormTabs' Protocol sub-tab bar so a reload can
    // restore it) only makes sense while 'tab=protocol' — leaving it in the URL when
    // switching to another top-level tab left it stranded there, and StudyFormTabs would
    // otherwise need every one of its URL-reading effects to independently know to ignore
    // a param that isn't really about them. Drop it here instead, once, at the source.
    if (slug !== 'protocol') {
      nextParams.delete('protocolSubTab');
    }
    navigate(`${studyFormBasePath}?${nextParams.toString()}`);
  };

  // Handle profile menu
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle notification menu
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  // Check if a route is active
  const isRouteActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Menu structure with nested items.
  // 'workflows' (Document Workflows) moved out of this generic row — it's now study-scoped
  // and rendered alongside the Library study-tab (see STUDY_TAB_ITEMS below) instead of here.
  const menuItems = [
    {
      id: 'home',
      text: 'Home',
      icon: <DashboardIcon sx={{ color: '#2196F3' }} />,
      path: '/',
      // Represented inside the study tabs row itself once a study is open — see
      // STUDY_TAB_ITEMS' 'app-home' entry — so it isn't duplicated out here too.
      hideWhenStudyOpen: true,
    },
    {
      id: 'about',
      text: 'About Us',
      icon: <BusinessIcon sx={{ color: '#168AAD' }} />,
      path: '/about',
      // Only relevant outside a study — hidden once the study tabs take over the header.
      hideWhenStudyOpen: true,
    },
    {
      id: 'auditTrail',
      text: 'Audit Trail',
      icon: <HistoryIcon sx={{ color: '#607D8B' }} />,
      path: '/audit-trail',
      ownerOnly: true,
    },
  ];

  // Restrict sidebar for reviewers — Home always shows regardless of role.
  const reviewerAllowedMenuIds = ['home'];
  const filteredMenuItems = (user?.role === 'reviewer'
    ? menuItems.filter(item => reviewerAllowedMenuIds.includes(item.id))
    : menuItems
  )
    .filter(item => !item.ownerOnly || isHubOwner)
    .filter(item => !(item.hideWhenStudyOpen && studyProjectId));

  // Flattened nav entries for the mobile dropdown menu
  const flatNavEntries = filteredMenuItems.map((item) => ({
    key: item.id,
    text: item.text,
    path: item.path,
    icon: item.icon,
  }));

  const handleNavMenuOpen = (event) => setNavMenuAnchorEl(event.currentTarget);
  const handleNavMenuClose = () => setNavMenuAnchorEl(null);

  // Top navbar item buttons (desktop)
  const topNavItems = (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
      {filteredMenuItems.map((item) => (
        <Button
          key={item.id}
          onClick={() => navigate(item.path)}
          sx={{
            color: '#ffffff',
            fontWeight: isRouteActive(item.path) ? 700 : 500,
            backgroundColor: isRouteActive(item.path) ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
          }}
        >
          {item.text}
        </Button>
      ))}
    </Box>
  );

  // Nav dropdown (mobile — replaces the sidebar drawer below the `md` breakpoint)
  const mobileNavMenu = (
    <Menu
      anchorEl={navMenuAnchorEl}
      open={Boolean(navMenuAnchorEl)}
      onClose={handleNavMenuClose}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
    >
      {flatNavEntries.map((entry) => (
        <MenuItem
          key={entry.key}
          selected={isRouteActive(entry.path)}
          onClick={() => {
            navigate(entry.path);
            handleNavMenuClose();
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>{entry.icon}</ListItemIcon>
          <ListItemText primary={entry.text} />
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        onClick={() => {
          navigate('/help');
          handleNavMenuClose();
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <HelpIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Help Center" />
      </MenuItem>
    </Menu>
  );

  // Profile menu
  const profileMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleProfileMenuClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 3,
        sx: { mt: 1, minWidth: 200 }
      }}
    >
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
        <ListItemAvatar>
          <Avatar 
            src={user?.picture} 
            sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
          >
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
        </ListItemAvatar>
        <Box sx={{ ml: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || 'user@example.com'}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <MenuItem onClick={() => navigate('/profile')}>
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Profile</ListItemText>
      </MenuItem>
      {/* <MenuItem onClick={() => navigate('/settings')}>
        <ListItemIcon>
          <SettingsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Settings</ListItemText>
      </MenuItem> */}
      <Divider />
      <MenuItem onClick={() => logout()}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  // Notification menu
  const notificationMenu = (
    <Menu
      anchorEl={notificationAnchorEl}
      open={Boolean(notificationAnchorEl)}
      onClose={handleNotificationMenuClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 3,
        sx: { mt: 1, minWidth: 320, maxWidth: 320 }
      }}
    >
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notifications</Typography>
        {!notificationsRead && (
          <Button size="small" sx={{ fontSize: '0.75rem' }} onClick={() => setNotificationsRead(true)}>
            Mark all as read
          </Button>
        )}
      </Box>
      {notificationsRead ? (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            You're all caught up.
          </Typography>
        </Box>
      ) : (
        <>
          <Divider />
          <MenuItem sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Box sx={{ mr: 1.5, mt: 0.5 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  <AssignmentIcon fontSize="small" />
                </Avatar>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Protocol Update Required
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  Study protocol TEST-001 needs review and approval
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  2 hours ago
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Box sx={{ mr: 1.5, mt: 0.5 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                  <Description fontSize="small" />
                </Avatar>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Document Approved
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  ICF Template v2.0 has been approved
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Yesterday
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        </>
      )}
    </Menu>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: colors.accent.teal,
            color: '#ffffff',
            boxShadow: shadow.header,
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={handleNavMenuOpen}
              sx={{
                mr: 1.5,
                display: { md: 'none' },
                color: '#ffffff',
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Mobile view - Study name */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mr: 2 }}>
              <StudySwitcher
                size="small"
                chipSx={{
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              />
            </Box>

            <Box
              onClick={() => navigate('/')}
              sx={{
                display: { xs: 'none', md: 'flex' },
                cursor: 'pointer',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                transition: `opacity ${transition.fast} ${transition.easing}`,
                '&:hover': { opacity: 0.85 },
              }}
            >
              <Box
                component="img"
                src={dizzarooLogo}
                alt="Dizzaroo"
                sx={{ height: 22, width: 'auto', display: 'block' }}
              />
              <Typography
                noWrap
                component="div"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: '#ffffff',
                  fontSize: '0.625rem',
                  mt: 0.25,
                }}
              >
                Document Authoring Tool
              </Typography>
            </Box>

            {studyProjectId && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: 'rgba(255, 255, 255, 0.35)', my: 1, mr: 2 }}
                />
                <Tabs
                  value={activeStudyTabSlug}
                  onChange={handleStudyTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  aria-label="study tabs"
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    minHeight: 40,
                    flex: '0 1 auto',
                    minWidth: 0,
                    mr: 2,
                    '& .MuiTabs-scrollButtons': {
                      color: '#ffffff',
                      '&.Mui-disabled': { opacity: 0.3 },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#ffffff',
                    },
                    '& .MuiTab-root': {
                      minHeight: 40,
                      textTransform: 'none',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      py: 1,
                      px: 1.5,
                      minWidth: 0,
                      '&.Mui-selected': {
                        color: '#ffffff',
                        fontWeight: 700,
                      },
                    },
                  }}
                >
                  {visibleStudyTabs.map((item) => (
                    <Tab key={item.slug} value={item.slug} label={item.label} />
                  ))}
                </Tabs>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: 'rgba(255, 255, 255, 0.35)', my: 1, mr: 2 }}
                />
              </>
            )}

            {topNavItems}

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <StudySwitcher
                size="medium"
                chipSx={{
                  mr: 0.75,
                  height: 34,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  px: 0.5,
                  bgcolor: 'rgba(255, 255, 255, 0.16)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '17px',
                }}
              />
              <Tooltip title="Help Center">
                <IconButton
                  onClick={() => navigate('/help')}
                  size="medium"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    color: '#ffffff',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Notifications">
                <IconButton
                  onClick={handleNotificationMenuOpen}
                  size="medium"
                  sx={{
                    color: '#ffffff',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
                  }}
                >
                  <Badge badgeContent={notificationsRead ? 0 : 2} color="error">
                    <NotificationsIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Account">
                <IconButton
                  edge="end"
                  onClick={handleProfileMenuOpen}
                  size="medium"
                  sx={{ ml: 0.5, p: 0.25 }}
                >
                  <Avatar
                    src={user?.picture}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {user?.name?.charAt(0) || 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {profileMenu}
        {notificationMenu}
        {mobileNavMenu}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            pt: 0, // Remove top padding since we have margin-top
            mt: '64px', // Fixed top margin to account for AppBar height
            width: '100%',
            minWidth: 0,
            overflowX: 'hidden',
            minHeight: '100vh',
            bgcolor: 'background.default',
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;